import { ATLAS_FINALISED_LABEL, billingFeeFor, billingFeeLines, isAtlasFinalised } from "@/lib/billingConstants";
import { buildWeekSummary } from "@/lib/billingWeekSummary";
import { buildLawyerQuestions } from "@/lib/lawyerQuestions";
import { prepClaimAmount, prepClaimFeeName, prepClaimsTotal } from "@/lib/prepClaims";
import { formatMoney } from "./BillingReportPanel";

// The printed weekly billing sheet: one table per appearance date, then a summary page.
// It is hidden on screen and shown only when printing (Print / Save PDF).
//
// One font throughout, with a deliberate size for each element. The selectors start at
// .lacw-billing-print-root so they beat the page's on-screen 13px rule.
export const BILLING_PRINT_CSS = `
        @media print {
          @page { size: A4 landscape; margin: 10mm; }
          body { background: white !important; }
          body * { visibility: hidden !important; }
          .lacw-print-sheet, .lacw-print-sheet * { visibility: visible !important; }
          /* One font throughout. Sizes are set per element below, and the selectors are
             specific enough to beat the page's on-screen 13px rule. */
          .lacw-billing-print-root .lacw-print-sheet,
          .lacw-billing-print-root .lacw-print-sheet * {
            font-family: Arial, Helvetica, sans-serif !important;
            font-size: 7.5pt !important;
            line-height: 1.35 !important;
            letter-spacing: normal !important;
            font-style: normal !important;
            color: #0f172a !important;
          }
          /* StandAloneGuidelines prints every span inside .flex.items-start (its checkbox rows)
             in Georgia 9.5pt with left padding, and this sheet sits inside such a box. These
             selectors outrank that rule, so the sheet's spans stay like the rest of it. */
          .lacw-billing-print-root section.lacw-print-sheet span {
            font-family: Arial, Helvetica, sans-serif !important;
            font-size: 7.5pt !important;
            line-height: 1.35 !important;
            font-weight: inherit !important;
            letter-spacing: normal !important;
            color: inherit !important;
            padding-left: 0 !important;
          }
          .lacw-billing-print-root section.lacw-print-sheet span.lacw-print-muted { color: #475569 !important; }
          .lacw-print-sheet {
            display: block !important;
            position: absolute;
            inset: 0;
            width: 100%;
            background: white !important;
            box-sizing: border-box !important;
          }
          .lacw-print-sheet table { width: 100%; table-layout: fixed; border-collapse: collapse; }
          .lacw-print-sheet thead { display: table-header-group; }
          .lacw-billing-print-root .lacw-print-sheet th,
          .lacw-billing-print-root .lacw-print-sheet td {
            border: 0.5pt solid #94a3b8 !important;
            padding: 3pt 4pt !important;
            vertical-align: top;
            text-align: left;
            white-space: normal !important;
            overflow-wrap: break-word;
            word-break: break-word;
            hyphens: auto;
          }
          .lacw-billing-print-root .lacw-print-sheet th {
            background: #e2e8f0 !important;
            font-size: 7pt !important;
            font-weight: 700 !important;
            text-transform: uppercase;
          }
          .lacw-print-sheet tbody tr:nth-child(even) td { background: #f8fafc !important; }
          .lacw-print-sheet .lacw-print-num { text-align: right !important; font-variant-numeric: tabular-nums; white-space: nowrap !important; }
          .lacw-print-sheet strong, .lacw-print-sheet b { font-weight: 700 !important; }
          .lacw-print-sheet .lacw-print-muted { color: #475569 !important; }
          .lacw-print-sheet .lacw-print-finalised { color: #047857 !important; margin-top: 2px; }

          .lacw-print-header {
            display: flex !important;
            justify-content: space-between;
            align-items: flex-end;
            border-bottom: 1.5pt solid #0c4a6e !important;
            padding-bottom: 4pt !important;
            margin-bottom: 6pt !important;
            break-after: avoid;
            page-break-after: avoid;
          }
          .lacw-billing-print-root .lacw-print-sheet .lacw-print-org { font-size: 7pt !important; font-weight: 700 !important; text-transform: uppercase; letter-spacing: 0.08em !important; color: #0c4a6e !important; }
          .lacw-billing-print-root .lacw-print-sheet .lacw-print-title { font-size: 15pt !important; font-weight: 700 !important; line-height: 1.2 !important; margin: 1pt 0 0 !important; }
          .lacw-print-meta { text-align: right; }

          .lacw-print-date-section { margin-top: 6pt; }
          .lacw-print-date-heading {
            display: flex !important;
            justify-content: space-between;
            align-items: baseline;
            background: #0c4a6e !important;
            padding: 3pt 5pt !important;
            break-after: avoid;
            page-break-after: avoid;
          }
          .lacw-billing-print-root .lacw-print-sheet .lacw-print-date-heading,
          .lacw-billing-print-root .lacw-print-sheet .lacw-print-date-heading * { color: white !important; }
          .lacw-billing-print-root .lacw-print-sheet .lacw-print-date-heading strong { font-size: 8.5pt !important; }
          .lacw-print-row { break-inside: avoid; page-break-inside: avoid; }
          .lacw-print-fee-line { display: flex; justify-content: space-between; gap: 4pt; }

          .lacw-print-summary { break-before: page; page-break-before: always; }
          .lacw-billing-print-root .lacw-print-sheet .lacw-print-summary h2 { font-size: 11pt !important; font-weight: 700 !important; margin: 0 0 4pt !important; color: #0c4a6e !important; }
          .lacw-print-summary-grid { display: flex !important; gap: 10mm; align-items: flex-start; margin-top: 6pt; }
          .lacw-print-summary-grid > div { flex: 1 1 0; }
          .lacw-print-questions { break-before: page; page-break-before: always; }
          .lacw-print-lawyer { margin-top: 8pt; break-inside: avoid-page; }
          .lacw-billing-print-root .lacw-print-sheet .lacw-print-lawyer h2 { font-size: 10pt !important; font-weight: 700 !important; margin: 0 0 3pt !important; color: #0c4a6e !important; }
          .lacw-billing-print-root .lacw-print-sheet .lacw-print-question { font-weight: 700 !important; }
          .lacw-print-summary tfoot td { background: #e2e8f0 !important; font-weight: 700 !important; }
          .lacw-print-tiles { display: flex !important; gap: 4mm; margin-bottom: 6pt; }
          .lacw-print-tile { flex: 1 1 0; border: 0.5pt solid #94a3b8; padding: 4pt 6pt; }
          .lacw-billing-print-root .lacw-print-sheet .lacw-print-tile b { display: block; font-size: 13pt !important; line-height: 1.2 !important; }
        }
`;

export default function BillingPrintSheet({ fileName, weekLabel, entries = [], groupedByDate = [], actions = {}, fees = {}, notes = {}, prepClaims = [] }) {
  return (
    <section className="lacw-print-sheet hidden">
      <div className="lacw-print-header">
        <div>
          <p className="lacw-print-org">Law and Advocacy Centre for Women</p>
          <h1 className="lacw-print-title">Weekly Billing Sheet</h1>
          <p className="lacw-print-muted">Source: {fileName || "Weekly diary"}</p>
        </div>
        <div className="lacw-print-meta">
          <p><strong>Week:</strong> {weekLabel}</p>
          <p><strong>Appearances:</strong> {entries.length}</p>
          <p><strong>Printed:</strong> {new Date().toLocaleDateString("en-AU")}</p>
        </div>
      </div>
      {groupedByDate.map(dateGroup => (
        <section key={`print-date-${dateGroup.date || "missing"}`} className="lacw-print-date-section">
          <div className="lacw-print-date-heading">
            <strong>{dateGroup.label}</strong>
            <span>{dateGroup.entries.length} appearance{dateGroup.entries.length === 1 ? "" : "s"}</span>
          </div>
          <table>
            <colgroup>
              <col style={{ width: "10%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "11%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Court</th>
                <th>Client</th>
                <th>Aid</th>
                <th>Appearance</th>
                <th>Carriage</th>
                <th>Counsel</th>
                <th>Outcome</th>
                <th>Action</th>
                <th className="lacw-print-num">Fee</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {dateGroup.entries.map(entry => {
                const lines = billingFeeLines(entry, actions[entry.id], fees[entry.id]);
                return (
                  <tr key={`print-${entry.id}`} className="lacw-print-row">
                    <td>{entry.court || "-"}</td>
                    <td><strong>{entry.client_name || "-"}</strong></td>
                    <td>
                      <strong>{entry.grant_type || "-"}</strong>
                      {entry.grant_type === "VU" && <div className="lacw-print-muted">Not confirmed</div>}
                      {entry.grant_type === "VC" && <div className="lacw-print-muted">Confirmed</div>}
                    </td>
                    <td>{entry.appearance_type || "-"}</td>
                    <td><strong>{entry.lawyer_initials || "-"}</strong></td>
                    <td>{entry.counsel_briefed || "-"}</td>
                    <td>{entry.outcome || "Further information required."}</td>
                    <td>
                      {actions[entry.id] || (isAtlasFinalised(fees[entry.id]) ? "" : "-")}
                      {isAtlasFinalised(fees[entry.id]) && (
                        <div className="lacw-print-finalised"><strong>✓ {ATLAS_FINALISED_LABEL}</strong></div>
                      )}
                    </td>
                    <td>
                      {lines.length === 0 && <div className="lacw-print-num">-</div>}
                      {lines.length === 1 && <div className="lacw-print-num">{formatMoney(lines[0].amount)}</div>}
                      {/* A single dated fee still shows what it is and its date. */}
                      {lines.length === 1 && lines[0].date && <div className="lacw-print-muted">{lines[0].label}</div>}
                      {lines.length > 1 && (
                        <>
                          {/* Several fees on one entry: each one, then the total. */}
                          {lines.map((line, i) => (
                            <div key={i} className="lacw-print-fee-line">
                              <span>{line.label}</span>
                              <span className="lacw-print-num">{formatMoney(line.amount)}</span>
                            </div>
                          ))}
                          <div className="lacw-print-fee-line">
                            <strong>Total</strong>
                            <strong className="lacw-print-num">{formatMoney(billingFeeFor(entry, actions[entry.id], fees[entry.id]))}</strong>
                          </div>
                        </>
                      )}
                    </td>
                    <td>{notes[entry.id] || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ))}

      {prepClaims.length > 0 && (
        <section className="lacw-print-date-section">
          <div className="lacw-print-date-heading">
            <strong>Preparation claims</strong>
            <span>{prepClaims.length} claim{prepClaims.length === 1 ? "" : "s"} · {formatMoney(prepClaimsTotal(prepClaims))}</span>
          </div>
          <table>
            <colgroup>
              <col style={{ width: "20%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "18%" }} />
            </colgroup>
            <thead>
              <tr><th>Client</th><th>Grant</th><th>Court level</th><th>Prep fee</th><th className="lacw-print-num">Amount</th><th>Note</th></tr>
            </thead>
            <tbody>
              {prepClaims.map(line => (
                <tr key={line.id} className="lacw-print-row">
                  <td><strong>{line.client || "-"}</strong></td>
                  <td>{line.grant || "-"}</td>
                  <td>{line.courtLevel || "-"}</td>
                  <td>{prepClaimFeeName(line) || "-"}</td>
                  <td className="lacw-print-num">{formatMoney(prepClaimAmount(line))}</td>
                  <td>{line.note || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {(() => {
        const summary = buildWeekSummary(entries, actions, fees, prepClaims);
        return (
          <section className="lacw-print-summary">
            <div className="lacw-print-header">
              <div>
                <p className="lacw-print-org">Law and Advocacy Centre for Women</p>
                <h1 className="lacw-print-title">Weekly Billing Summary</h1>
              </div>
              <div className="lacw-print-meta">
                <p><strong>Week:</strong> {weekLabel}</p>
              </div>
            </div>
            <div className="lacw-print-tiles">
              <div className="lacw-print-tile">Appearances this week<b>{summary.totalAppearances}</b></div>
              <div className="lacw-print-tile">Claimed appearances<b>{summary.claimedCount} · {formatMoney(summary.claimedTotal)}</b></div>
              <div className="lacw-print-tile">Preparation claims<b>{summary.prepCount} · {formatMoney(summary.prepTotal)}</b></div>
              <div className="lacw-print-tile">Total claimed this week<b>{formatMoney(summary.weekTotal)}</b></div>
              <div className="lacw-print-tile">Not a money claim<b>{summary.notClaimedCount}</b></div>
            </div>
            <div className="lacw-print-summary-grid">
              <div>
                <h2>Claimed appearances</h2>
                <table>
                  <colgroup>
                    <col style={{ width: "60%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "25%" }} />
                  </colgroup>
                  <thead>
                    <tr><th>Appearance type</th><th className="lacw-print-num">Number</th><th className="lacw-print-num">Value</th></tr>
                  </thead>
                  <tbody>
                    {summary.claimed.length === 0 && <tr><td colSpan={3}>No claimed appearances this week.</td></tr>}
                    {summary.claimed.map(row => (
                      <tr key={row.type}>
                        <td>{row.type}{row.withoutFee > 0 && <span className="lacw-print-muted"> ({row.withoutFee} with no listed fee)</span>}</td>
                        <td className="lacw-print-num">{row.count}</td>
                        <td className="lacw-print-num">{row.withoutFee === row.count ? "-" : formatMoney(row.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><td>Total claimed</td><td className="lacw-print-num">{summary.claimedCount}</td><td className="lacw-print-num">{formatMoney(summary.claimedTotal)}</td></tr>
                  </tfoot>
                </table>
                {summary.claimedWithoutFee > 0 && (
                  <p className="lacw-print-muted" style={{ marginTop: "3pt" }}>{summary.claimedWithoutFee} claimed appearance{summary.claimedWithoutFee === 1 ? " has" : "s have"} no listed fee and {summary.claimedWithoutFee === 1 ? "is" : "are"} not included in the total.</p>
                )}
              </div>
              <div>
                <h2>Not a money claim</h2>
                <table>
                  <colgroup>
                    <col style={{ width: "75%" }} />
                    <col style={{ width: "25%" }} />
                  </colgroup>
                  <thead>
                    <tr><th>Action</th><th className="lacw-print-num">Number</th></tr>
                  </thead>
                  <tbody>
                    {summary.notClaimed.length === 0 && <tr><td colSpan={2}>Every appearance this week is claimed.</td></tr>}
                    {summary.notClaimed.map(row => (
                      <tr key={row.action}>
                        <td>{row.action}</td>
                        <td className="lacw-print-num">{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><td>Total not claimed</td><td className="lacw-print-num">{summary.notClaimedCount}</td></tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </section>
        );
      })()}

      {(() => {
        // Questions for lawyers: notes containing "?" or rows set to Query, per lawyer.
        const groups = buildLawyerQuestions(entries, actions, notes);
        if (groups.length === 0) return null;
        const total = groups.reduce((sum, group) => sum + group.items.length, 0);
        return (
          <section className="lacw-print-questions">
            <div className="lacw-print-header">
              <div>
                <p className="lacw-print-org">Law and Advocacy Centre for Women</p>
                <h1 className="lacw-print-title">Questions for Lawyers</h1>
              </div>
              <div className="lacw-print-meta">
                <p><strong>Week:</strong> {weekLabel}</p>
                <p><strong>Questions:</strong> {total}</p>
              </div>
            </div>
            {groups.map(group => (
              <div key={group.lawyer} className="lacw-print-lawyer">
                <h2>{group.lawyer} · {group.items.length} question{group.items.length === 1 ? "" : "s"}</h2>
                <table>
                  <colgroup>
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "23%" }} />
                    <col style={{ width: "30%" }} />
                  </colgroup>
                  <thead>
                    <tr><th>Date</th><th>Client</th><th>Court</th><th>Appearance</th><th>Outcome</th><th>Question</th></tr>
                  </thead>
                  <tbody>
                    {group.items.map(({ entry, note, action }) => (
                      <tr key={entry.id} className="lacw-print-row">
                        <td>{entry.date || "-"}</td>
                        <td><strong>{entry.client_name || "-"}</strong></td>
                        <td>{entry.court || "-"}</td>
                        <td>{entry.appearance_type || "-"}</td>
                        <td>{entry.outcome || "-"}</td>
                        <td className="lacw-print-question">{note || (action === "Query" ? "Set to Query - no note written yet." : "-")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </section>
        );
      })()}
    </section>
  );
}
